import React, { useRef, useState } from "react";
import "./Demo.css";
import { useTranslation, Trans } from "react-i18next";
import { sendContactEmail } from "../../services/email_service";

export default function Demo() {
  const { t } = useTranslation();

  const [offer, setOffer] = useState("compte-demo"); // "compte-demo" | "sur-site"
  const [plan, setPlan] = useState(""); // plan d’abo si "sur-site"
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const requestInFlight = useRef(false);

  const onOfferChange = (e) => {
    const value = e.target.value;
    setOffer(value);
    if (value !== "sur-site") setPlan("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestInFlight.current) return;
    setSubmitError("");

    const data = new FormData(e.currentTarget);
    const payload = Object.fromEntries(data.entries());

    // Validation minimale
    const nextErrors = {};
    if (!payload.nom?.trim()) nextErrors.nom = t("demo.validation.required");
    if (!payload.prenom?.trim()) nextErrors.prenom = t("demo.validation.required");
    if (!payload.email?.trim()) nextErrors.email = t("demo.validation.required");
    if (!payload.telephone?.trim()) nextErrors.telephone = t("demo.validation.required");
    if (!payload.entreprise?.trim()) nextErrors.entreprise = t("demo.validation.required");
    if (!payload.role?.trim()) nextErrors.role = t("demo.validation.required");
    if (!payload.nbAscenseurs?.trim()) nextErrors.nbAscenseurs = t("demo.validation.required");
    if (offer === "sur-site" && !payload.plan) nextErrors.plan = t("demo.validation.choose_plan");

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    // Contrat de l'API Contact déjà validée : pas de nouveau champ côté serveur.
    // Les informations spécifiques à Demo sont conservées dans le message.
    const planLabels = {
      desincarceration: t("demo.form.plan.options.desincarceration"),
      depannage: t("demo.form.plan.options.depannage"),
      maintenance: t("demo.form.plan.options.maintenance"),
    };
    const messageLines = [
      t("demo.form.title"),
      `${t("demo.form.fields.role.label")} : ${payload.role.trim()}`,
      `${t("demo.form.fields.nbAscenseurs.label")} : ${payload.nbAscenseurs.trim()}`,
      `${t("demo.form.offer.legend")} : ${offer === "sur-site"
        ? t("demo.form.offer.onsite_label")
        : t("demo.form.offer.demo_label")}`,
    ];
    if (offer === "sur-site") {
      messageLines.push(
        `${t("demo.form.plan.label")} : ${planLabels[payload.plan] || payload.plan}`
      );
    }

    const apiPayload = {
      firstname: payload.prenom.trim(),
      name: payload.nom.trim(),
      email: payload.email.trim(),
      telephone: payload.telephone.trim(),
      company: payload.entreprise.trim(),
      subject: "demo",
      message: messageLines.join("\n"),
    };

    // Capture avant await : currentTarget n'est pas garanti après l'asynchrone.
    const formElement = e.currentTarget;
    requestInFlight.current = true;
    try {
      setSending(true);
      await sendContactEmail(apiPayload);
      alert(t("demo.alert_success"));
      formElement.reset();
      setOffer("compte-demo");
      setPlan("");
    } catch (err) {
      setSubmitError(t("demo.submit_error"));
    } finally {
      requestInFlight.current = false;
      setSending(false);
    }
  };

  return (
    <main className="page page-demo" aria-labelledby="demo-title">
      {/* ===== Hero court (fond 100% blanc) ===== */}
      <section className="demo-hero" role="region">
        <div className="demo-hero-inner">
          <header className="demo-hero-header">
            <h1 id="demo-title" className="demo-title">
              {t("demo.hero.title")}
            </h1>
            <p className="demo-subtitle">
              {t("demo.hero.subtitle")}
            </p>
          </header>
        </div>
      </section>

      {/* ===== Options : texte d’explication ===== */}
      <section className="demo-options" aria-label={t("demo.options.aria")}>
        <div className="container">
          <div className="options-grid">
            <article className="option-card" aria-labelledby="card-demo">
              <h2 id="card-demo" className="option-title">
                {t("demo.options.demo.title")}
              </h2>

              <p className="option-text">
                <Trans
                  i18nKey="demo.options.demo.text_html"
                  components={{ strong: <strong /> }}
                />
              </p>

              <ul className="option-bullets">
                <li>{t("demo.options.demo.bullets.b1")}</li>
                <li>{t("demo.options.demo.bullets.b2")}</li>
                <li>{t("demo.options.demo.bullets.b3")}</li>
              </ul>
            </article>

            <article className="option-card" aria-labelledby="card-onsite">
              <h2 id="card-onsite" className="option-title">
                {t("demo.options.onsite.title")}
              </h2>

              <p className="option-text">
                <Trans
                  i18nKey="demo.options.onsite.text1_html"
                  components={{ strong: <strong /> }}
                />
              </p>

              <p className="option-text">
                <Trans
                  i18nKey="demo.options.onsite.text2_html"
                  components={{ strong: <strong /> }}
                />
              </p>

              <ul className="option-bullets" aria-label={t("demo.options.onsite.list_aria")}>
                <li>{t("demo.options.onsite.bullets.b1")}</li>
                <li>{t("demo.options.onsite.bullets.b2")}</li>
                <li>{t("demo.options.onsite.bullets.b3")}</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ===== Formulaire ===== */}
      <section className="demo-form-section" aria-labelledby="form-title">
        <div className="container">
          <header className="form-header">
            <h2 id="form-title" className="form-title">
              {t("demo.form.title")}
            </h2>
            <p className="form-deck">
              {t("demo.form.deck")}
            </p>
          </header>

          <form className="demo-form" onSubmit={handleSubmit} noValidate>
            {/* Informations contact */}
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="prenom">{t("demo.form.fields.prenom.label")}</label>
                <input id="prenom" name="prenom" autoComplete="given-name" />
                {errors.prenom && <p className="field-error">{errors.prenom}</p>}
              </div>

              <div className="form-field">
                <label htmlFor="nom">{t("demo.form.fields.nom.label")}</label>
                <input id="nom" name="nom" autoComplete="family-name" />
                {errors.nom && <p className="field-error">{errors.nom}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="email">{t("demo.form.fields.email.label")}</label>
                <input id="email" type="email" name="email" autoComplete="email" />
                {errors.email && <p className="field-error">{errors.email}</p>}
              </div>

              <div className="form-field">
                <label htmlFor="telephone">{t("demo.form.fields.telephone.label")}</label>
                <input id="telephone" name="telephone" autoComplete="tel" />
                {errors.telephone && <p className="field-error">{errors.telephone}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="entreprise">{t("demo.form.fields.entreprise.label")}</label>
                <input id="entreprise" name="entreprise" autoComplete="organization" />
                {errors.entreprise && <p className="field-error">{errors.entreprise}</p>}
              </div>

              <div className="form-field">
                <label htmlFor="role">{t("demo.form.fields.role.label")}</label>
                <input
                  id="role"
                  name="role"
                  placeholder={t("demo.form.fields.role.placeholder")}
                />
                {errors.role && <p className="field-error">{errors.role}</p>}
              </div>
            </div>

            {/* Taille de parc */}
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="nbAscenseurs">{t("demo.form.fields.nbAscenseurs.label")}</label>
                <input id="nbAscenseurs" name="nbAscenseurs" inputMode="numeric" />
                {errors.nbAscenseurs && <p className="field-error">{errors.nbAscenseurs}</p>}
              </div>
              <div className="form-field empty" aria-hidden="true" />
            </div>

            {/* Choix d’offre */}
            <fieldset className="fieldset" role="radiogroup" aria-labelledby="offer-legend">
              <legend id="offer-legend">{t("demo.form.offer.legend")}</legend>
              <div className="radio-group">
                <label className="radio">
                  <input
                    type="radio"
                    name="offer"
                    value="compte-demo"
                    checked={offer === "compte-demo"}
                    onChange={onOfferChange}
                  />
                  {t("demo.form.offer.demo_label")}
                </label>

                <label className="radio">
                  <input
                    type="radio"
                    name="offer"
                    value="sur-site"
                    checked={offer === "sur-site"}
                    onChange={onOfferChange}
                  />
                  {t("demo.form.offer.onsite_label")}
                </label>
              </div>
            </fieldset>

            {/* Sélection d’abonnement si “sur site” */}
            {offer === "sur-site" && (
              <div className="form-row" style={{ marginTop: 10 }}>
                <div className="form-field">
                  <label htmlFor="plan">{t("demo.form.plan.label")}</label>
                  <select
                    id="plan"
                    name="plan"
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                  >
                    <option value="">{t("demo.form.plan.placeholder")}</option>
                    <option value="desincarceration">{t("demo.form.plan.options.desincarceration")}</option>
                    <option value="depannage">{t("demo.form.plan.options.depannage")}</option>
                    <option value="maintenance">{t("demo.form.plan.options.maintenance")}</option>
                  </select>

                  {errors.plan && <p className="field-error">{errors.plan}</p>}

                  <p className="field-hint">
                    {t("demo.form.plan.hint")}
                  </p>
                </div>

                <div className="form-field empty" aria-hidden="true" />
              </div>
            )}

            {/* Actions */}
            <div className="form-actions">
              <button type="submit" className="btn-solid" disabled={sending}>
                {sending ? t("demo.form.submit.sending") : t("demo.form.submit.label")}
              </button>

              {submitError && (
                <p className="submit-error" role="alert">{submitError}</p>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
